import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, 
  Image as ImageIcon, 
  Award, 
  Heart, 
  Mail, 
  Archive,
  ArchiveRestore,
  Trash2,
  Plus,
  Download,
  X,
  Calendar,
  MapPin,
  AlertTriangle,
  WifiOff,
  Eye,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useOffline } from '../contexts/OfflineContext';
import { ArchiveHelperText } from './archive/ArchiveHelperText';
import { ArchiveEmptyState } from './archive/ArchiveEmptyState';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { CORE_JOURNEY_UI } from '../config/testerRelease';
import { CORE_EMPTY_STATES } from '../content/coreJourneyContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Document type icons and labels (gentle language)
const DOCUMENT_TYPES = {
  photo: { icon: ImageIcon, label: 'Photo', description: 'A captured moment' },
  birth_certificate: { icon: Award, label: 'Birth Certificate', description: 'Birth record' },
  marriage_certificate: { icon: Heart, label: 'Marriage Certificate', description: 'Marriage record' },
  death_certificate: { icon: FileText, label: 'Death Certificate', description: 'Death record' },
  letter: { icon: Mail, label: 'Letter', description: 'Handwritten correspondence' },
  record: { icon: FileText, label: 'Record', description: 'Official document' },
  artifact: { icon: FileText, label: 'Artifact', description: 'Other meaningful item' }
};

function documentScopeLabel(doc, profilePersonName) {
  const v = doc.visibility || 'person';
  if (v === 'private') return 'Private to me';
  if (v === 'shared') return 'Shared family archive';
  const name = (profilePersonName || '').trim();
  if (name) return `${name}'s profile`;
  return 'Person profile';
}

const DocumentsTab = ({ personId, personName, documents, onDocumentsChange, canEdit }) => {
  const { isOnline } = useOffline();
  const lifeLabel = personName ? `${personName}'s life` : "this person's life";
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    document_type: 'artifact',
    document_date: '',
    document_year: '',
    date_precision: 'unknown',
    location: '',
    source: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  /** Where the document belongs: person profile, shared archive, or private to uploader */
  const [documentVisibility, setDocumentVisibility] = useState('person');

  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      description: '',
      document_type: 'artifact',
      document_date: '',
      document_year: '',
      date_precision: 'unknown',
      location: '',
      source: '',
    });
    setDocumentVisibility('person');
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const docExtensions = ['jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'avif', 'heic', 'heif', 'pdf'];
    const ext = (file.name || '').toLowerCase().split('.').pop();
    const validTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/avif',
      'application/pdf', 'image/heic', 'image/heif',
    ];
    if (!validTypes.includes(file.type) && !docExtensions.includes(ext)) {
      toast.error('Please select an image (JPG, PNG, WebP, GIF, AVIF, HEIC) or PDF');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }

    // Auto-fill title from filename
    if (!uploadForm.title) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setUploadForm(prev => ({ ...prev, title: nameWithoutExt }));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    if (!uploadForm.title.trim()) {
      toast.error('Please add a title for this artifact');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('document_type', uploadForm.document_type);
      formData.append('document_date', uploadForm.document_date);
      formData.append('document_year', uploadForm.document_year);
      formData.append('date_precision', uploadForm.date_precision);
      formData.append('location', uploadForm.location);
      formData.append('source', uploadForm.source);
      formData.append('document_visibility', documentVisibility);

      await axios.post(`${API}/documents/${personId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document preserved!');
      resetUploadForm();
      setShowUploadDialog(false);
      onDocumentsChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to preserve document');
    } finally {
      setUploading(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedDocument) return;
    
    try {
      await axios.post(`${API}/documents/${selectedDocument.id}/archive`);
      toast.success('Document archived. You can restore it anytime.');
      setShowArchiveDialog(false);
      setSelectedDocument(null);
      onDocumentsChange?.();
    } catch (error) {
      toast.error('Unable to archive document');
    }
  };

  const handleRestore = async (docId) => {
    try {
      await axios.post(`${API}/documents/${docId}/restore`);
      toast.success('Document restored!');
      onDocumentsChange?.();
    } catch (error) {
      toast.error('Unable to restore document');
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      await axios.delete(`${API}/documents/${selectedDocument.id}`);
      toast.success('Document permanently deleted');
      setShowDeleteDialog(false);
      setSelectedDocument(null);
      setDeleteAcknowledged(false);
      onDocumentsChange?.();
    } catch (error) {
      toast.error('Unable to delete document');
    }
  };

  const openViewDialog = (doc) => {
    setSelectedDocument(doc);
    setShowViewDialog(true);
  };

  const openArchiveDialog = (doc) => {
    setSelectedDocument(doc);
    setShowArchiveDialog(true);
  };

  const handleReorder = async (doc, direction) => {
    if (!canEdit) return;
    try {
      await axios.post(`${API}/documents/${doc.id}/reorder`, { direction });
      toast.success('Order updated');
      onDocumentsChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to change order');
    }
  };

  const openDeleteDialog = (doc) => {
    setSelectedDocument(doc);
    setShowDeleteDialog(true);
    setDeleteAcknowledged(false);
  };

  const getDocTypeInfo = (type) => DOCUMENT_TYPES[type] || DOCUMENT_TYPES.artifact;
  
  const formatDate = (date, year, precision) => {
    if (date) return date;
    if (year) return precision === 'approximate' ? `circa ${year}` : `${year}`;
    return null;
  };

  const activeDocuments = documents.filter(d => !d.archived);
  const archivedDocuments = documents.filter(d => d.archived);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8">
      <header className="space-y-4 border-b border-[hsl(38_14%_88%)]/70 pb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
          <div className="min-w-0 max-w-2xl space-y-2">
            <h2 className="font-serif text-xl font-medium tracking-tight text-[hsl(24_12%_18%)]">Documents</h2>
            <ArchiveHelperText>
              Preserve photographs, certificates, letters, and keepsake papers connected to {lifeLabel}.
            </ArchiveHelperText>
          </div>
          {canEdit && (
            <div className="shrink-0 sm:pt-0.5">
              {isOnline ? (
                <Button
                  onClick={() => setShowUploadDialog(true)}
                  className="gap-2 rounded-full"
                  data-testid="add-document-btn"
                >
                  <Plus className="h-4 w-4" />
                  Preserve a document
                </Button>
              ) : (
                <div className="flex max-w-md items-start gap-2 rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                  <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>You&apos;ll be able to preserve new documents once you&apos;re back online.</span>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Empty — inline archival block (no floating hints) */}
      {activeDocuments.length === 0 && (
        <ArchiveEmptyState
          icon={FileText}
          align="start"
          title={CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noPhotos.title : 'Preserve a photograph, letter, certificate or keepsake.'}
          description={
            CORE_JOURNEY_UI
              ? CORE_EMPTY_STATES.noPhotos.description
              : `Link it to ${lifeLabel}'s life — each one stays part of their private archive.`
          }
          data-testid="no-documents"
          actions={
            canEdit && isOnline ? (
              <Button variant="outline" className="rounded-full border-[hsl(38_14%_82%)]" onClick={() => setShowUploadDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {CORE_JOURNEY_UI ? CORE_EMPTY_STATES.noPhotos.cta : 'Preserve a document'}
              </Button>
            ) : canEdit && !isOnline ? (
              <p className="text-sm text-muted-foreground">You'll be able to preserve documents once you're back online.</p>
            ) : (
              <p className="text-sm text-muted-foreground">Documents added by an editor will appear here.</p>
            )
          }
        />
      )}

      {/* Documents Grid */}
      {activeDocuments.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
          {activeDocuments.map(doc => {
            const typeInfo = getDocTypeInfo(doc.document_type);
            const IconComponent = typeInfo.icon;
            const isImage = ['jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'avif', 'heic', 'heif'].includes(doc.file_type?.toLowerCase());
            
            return (
              <div
                key={doc.id}
                className="group relative bg-card border border-border/50 rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
                onClick={() => openViewDialog(doc)}
                data-testid={`document-card-${doc.id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-muted/30 flex items-center justify-center overflow-hidden">
                  {isImage ? (
                    <img
                      src={resolveAssetUrl(BACKEND_URL, doc.file_url)}
                      alt={doc.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <FileText className="h-12 w-12 text-muted-foreground/40" />
                  )}
                </div>
                
                {/* Type Badge */}
                <div className="absolute top-2 left-2">
                  <div className="px-2 py-1 bg-background/90 backdrop-blur-sm rounded-md text-xs font-medium flex items-center gap-1.5">
                    <IconComponent className="h-3 w-3" />
                    <span>{typeInfo.label}</span>
                  </div>
                </div>

                {canEdit && isOnline && (
                  <div className="absolute top-2 right-2 flex flex-col gap-0.5">
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 rounded-md bg-background/90 shadow-sm"
                      aria-label="Move earlier in list"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReorder(doc, 'earlier');
                      }}
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 rounded-md bg-background/90 shadow-sm"
                      aria-label="Move later in list"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReorder(doc, 'later');
                      }}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}

                {/* Title & Date */}
                <div className="p-3">
                  <h4 className="font-medium text-sm truncate">{doc.title}</h4>
                  <p className="text-[11px] text-muted-foreground/75 mt-0.5 truncate">
                    {documentScopeLabel(doc, personName)}
                  </p>
                  {(doc.document_date || doc.document_year) && (
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {formatDate(doc.document_date, doc.document_year, doc.date_precision)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Archived Documents */}
      {archivedDocuments.length > 0 && (
        <div className="pt-6 border-t border-border/40">
          <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Archived ({archivedDocuments.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 opacity-60">
            {archivedDocuments.map(doc => (
              <div
                key={doc.id}
                className="p-3 bg-muted/30 rounded-lg flex items-center justify-between"
              >
                <span className="text-sm truncate flex-1">{doc.title}</span>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRestore(doc.id)}
                    className="ml-2"
                    data-testid={`restore-doc-${doc.id}`}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) resetUploadForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-document-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Preserve a Document</DialogTitle>
            <DialogDescription>
              Add something meaningful to {personName}&apos;s archive — a photograph, letter, or paper that carries memory.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-[#c9a961]/20 bg-[#c9a961]/5 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
            <p className="font-medium text-foreground/80">Before you save, consider:</p>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5">
              <li>What is this?</li>
              <li>Who is connected to it?</li>
              <li>Why does it matter?</li>
              <li>What memory does it carry?</li>
            </ul>
          </div>

          <div className="space-y-5 py-4">
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Where should this document live?</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Choose who this document belongs to and who should be able to see it.
              </p>
              <div className="grid gap-2 sm:grid-cols-1">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-2 hover:bg-background/80 has-[:checked]:border-primary/30 has-[:checked]:bg-background">
                  <input
                    type="radio"
                    name="doc-visibility"
                    className="mt-1"
                    checked={documentVisibility === 'person'}
                    onChange={() => setDocumentVisibility('person')}
                  />
                  <span>
                    <span className="text-sm font-medium">This person&apos;s profile</span>
                    <span className="block text-xs text-muted-foreground">Visible to people who can access this archive.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-2 hover:bg-background/80 has-[:checked]:border-primary/30 has-[:checked]:bg-background">
                  <input
                    type="radio"
                    name="doc-visibility"
                    className="mt-1"
                    checked={documentVisibility === 'shared'}
                    onChange={() => setDocumentVisibility('shared')}
                  />
                  <span>
                    <span className="text-sm font-medium">Shared family archive</span>
                    <span className="block text-xs text-muted-foreground">Shared family papers (wedding photos, census pages, letters).</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-transparent p-2 hover:bg-background/80 has-[:checked]:border-primary/30 has-[:checked]:bg-background">
                  <input
                    type="radio"
                    name="doc-visibility"
                    className="mt-1"
                    checked={documentVisibility === 'private'}
                    onChange={() => setDocumentVisibility('private')}
                  />
                  <span>
                    <span className="text-sm font-medium">Private to me</span>
                    <span className="block text-xs text-muted-foreground">Only you can see this; it won&apos;t appear in shared views or exports for others.</span>
                  </span>
                </label>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-sm font-medium">Select File</Label>
              <div className="mt-2">
                {previewUrl ? (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full max-h-48 object-contain rounded-lg bg-muted/20"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : selectedFile ? (
                  <div className="p-4 bg-muted/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-border/50 rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors">
                    <Plus className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Choose a photo, scan, or PDF</span>
                    <span className="text-xs text-muted-foreground/60 mt-1">Max 10MB</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/avif,.jfif,application/pdf,image/heic,image/heif"
                      className="hidden"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Document Type */}
            <div>
              <Label>What is this?</Label>
              <Select
                value={uploadForm.document_type}
                onValueChange={(value) => setUploadForm(prev => ({ ...prev, document_type: value }))}
              >
                <SelectTrigger className="mt-2" data-testid="document-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOCUMENT_TYPES).map(([key, { label, description }]) => (
                    <SelectItem key={key} value={key}>
                      <span className="font-medium">{label}</span>
                      <span className="text-muted-foreground ml-2">— {description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label>Name this document</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Wedding certificate, Mum's letter from 1962"
                className="mt-2"
                data-testid="document-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Why does it matter? <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What memory does it carry? What should future family understand?"
                rows={2}
                className="mt-2"
              />
            </div>

            {/* Date */}
            <div className="space-y-3">
              <Label>When is this from?</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Exact date (if known)</Label>
                  <Input
                    type="date"
                    value={uploadForm.document_date}
                    onChange={(e) => setUploadForm(prev => ({ 
                      ...prev, 
                      document_date: e.target.value,
                      date_precision: e.target.value ? 'exact' : prev.date_precision
                    }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Or just the year</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 1965"
                    value={uploadForm.document_year}
                    onChange={(e) => setUploadForm(prev => ({ 
                      ...prev, 
                      document_year: e.target.value,
                      date_precision: e.target.value ? 'year' : prev.date_precision
                    }))}
                    min="1000"
                    max="2100"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="approximate-date"
                  checked={uploadForm.date_precision === 'approximate'}
                  onCheckedChange={(checked) => 
                    setUploadForm(prev => ({ 
                      ...prev, 
                      date_precision: checked ? 'approximate' : (prev.document_date ? 'exact' : prev.document_year ? 'year' : 'unknown')
                    }))
                  }
                />
                <label htmlFor="approximate-date" className="text-sm text-muted-foreground cursor-pointer">
                  This date is approximate (circa)
                </label>
              </div>
              <p className="text-xs text-muted-foreground/70">
                It's okay if you don't know the exact date — any information helps.
              </p>
            </div>

            {/* Location */}
            <div>
              <Label>Location (optional)</Label>
              <Input
                value={uploadForm.location}
                onChange={(e) => setUploadForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Where was this taken or created?"
                className="mt-2"
              />
            </div>

            {/* Source */}
            <div>
              <Label>Source (optional)</Label>
              <Input
                value={uploadForm.source}
                onChange={(e) => setUploadForm(prev => ({ ...prev, source: e.target.value }))}
                placeholder="e.g. Grandmother's attic, Family Bible"
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile} data-testid="save-document-btn">
              {uploading ? 'Preserving...' : 'Preserve Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={showViewDialog} onOpenChange={(open) => {
        setShowViewDialog(open);
        if (!open) setSelectedDocument(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-document-dialog">
          {selectedDocument && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">{selectedDocument.title}</DialogTitle>
                <DialogDescription>
                  {getDocTypeInfo(selectedDocument.document_type).label}
                  {selectedDocument.document_date || selectedDocument.document_year ? (
                    <span className="ml-2">
                      • {formatDate(selectedDocument.document_date, selectedDocument.document_year, selectedDocument.date_precision)}
                    </span>
                  ) : null}
                </DialogDescription>
              </DialogHeader>

              {/* Document Preview */}
              <div className="my-4">
                {['jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'avif', 'heic', 'heif'].includes(selectedDocument.file_type?.toLowerCase()) ? (
                  <div className="bg-muted/20 rounded-xl p-4 flex items-center justify-center">
                    <img
                      src={resolveAssetUrl(BACKEND_URL, selectedDocument.file_url)}
                      alt={selectedDocument.title}
                      className="max-w-full max-h-[50vh] object-contain rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="bg-muted/20 rounded-xl p-8 text-center">
                    <FileText className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">PDF Document</p>
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className="space-y-3 text-sm">
                {selectedDocument.description && (
                  <p className="text-muted-foreground">{selectedDocument.description}</p>
                )}
                
                {selectedDocument.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{selectedDocument.location}</span>
                  </div>
                )}
                
                {selectedDocument.source && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Archive className="h-4 w-4" />
                    <span>From: {selectedDocument.source}</span>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                <a
                  href={resolveAssetUrl(BACKEND_URL, selectedDocument.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <Button variant="outline" className="w-full sm:w-auto">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </a>
                
                {canEdit && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowViewDialog(false);
                        openArchiveDialog(selectedDocument);
                      }}
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </Button>
                  </>
                )}
                
                <Button onClick={() => setShowViewDialog(false)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={(open) => {
        setShowArchiveDialog(open);
        if (!open) setSelectedDocument(null);
      }}>
        <DialogContent className="max-w-md" data-testid="archive-document-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Archive this document?
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Archiving hides this document from view without deleting it. 
              You can restore it anytime.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 my-4">
            <p className="text-sm text-muted-foreground">
              <strong>"{selectedDocument?.title}"</strong> will be moved to archived documents.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleArchive} className="flex-1" data-testid="confirm-archive-doc-btn">
              Archive
            </Button>
          </DialogFooter>
          
          <div className="border-t pt-4 mt-2">
            <button 
              onClick={() => {
                setShowArchiveDialog(false);
                openDeleteDialog(selectedDocument);
              }}
              className="text-sm text-muted-foreground hover:text-destructive transition-colors underline"
            >
              I need to permanently delete this document instead
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open);
        if (!open) {
          setSelectedDocument(null);
          setDeleteAcknowledged(false);
        }
      }}>
        <DialogContent className="max-w-md" data-testid="delete-document-dialog">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <DialogTitle className="font-serif text-xl text-center">
              Permanently Delete?
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              This action is <strong>irreversible</strong>. The document will be permanently erased.
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="bg-muted/50 rounded-xl p-4 my-4">
              <p className="text-sm font-medium">{selectedDocument.title}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {getDocTypeInfo(selectedDocument.document_type).label}
                {selectedDocument.document_date || selectedDocument.document_year ? (
                  <span> • {formatDate(selectedDocument.document_date, selectedDocument.document_year, selectedDocument.date_precision)}</span>
                ) : null}
              </p>
            </div>
          )}

          {/* Acknowledgment */}
          <div className="border-t pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox 
                checked={deleteAcknowledged}
                onCheckedChange={setDeleteAcknowledged}
                className="mt-0.5"
                data-testid="delete-doc-acknowledge"
              />
              <span className="text-sm text-muted-foreground">
                I understand that deleting this document is permanent and it cannot be recovered.
              </span>
            </label>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteAcknowledged(false);
              }} 
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={!deleteAcknowledged}
              className="flex-1"
              data-testid="confirm-delete-doc-btn"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsTab;
